import type {
  ConversationInteractiveButtonItem,
  ConversationInteractiveButtons,
  ConversationInteractiveList,
  ConversationInteractiveListRow,
  ConversationInteractiveListSection,
} from '../../conversations/types/Conversation';
import type {
  AutomationActionFailureMode,
  AutomationCondition,
  AutomationConditionLogic,
  AutomationConditionRequest,
} from '../../automations/types/AutomationCondition';
import {
  flattenConditionKeywords,
  normalizeActionFailureMode,
  normalizeAutomationConditions,
  normalizeConditionLogic,
  normalizeKeywordMatchMode,
} from '../../automations/types/AutomationCondition';

export type CommentAutomationChannel = 'instagram' | 'messenger';
export type CommentAutomationMatchMode = 'equals' | 'contains' | 'starts_with';
export type CommentAutomationActionType = 'public_comment_reply' | 'private_reply';
export type CommentAutomationBodySource = 'template' | 'ai' | 'interactive';
export type CommentAutomationInteractiveMode = 'list' | 'buttons';

export type CommentAutomationAction = {
  type: CommentAutomationActionType;
  bodySource: CommentAutomationBodySource;
  templateText?: string | null;
  fallbackTemplateText?: string | null;
  instruction?: string | null;
  promptUseCase?: string | null;
  interactiveMode?: CommentAutomationInteractiveMode | null;
  interactiveList?: ConversationInteractiveList | null;
  interactiveButtons?: ConversationInteractiveButtons | null;
};

export type CommentAutomationRule = {
  id: string;
  name: string;
  channel: CommentAutomationChannel;
  channelAccountId?: string | null;
  isEnabled: boolean;
  priority: number;
  matchMode: CommentAutomationMatchMode;
  keywords: string[];
  conditionLogic: AutomationConditionLogic;
  conditions: AutomationCondition[];
  onActionFailure: AutomationActionFailureMode;
  externalContentId?: string | null;
  actions: CommentAutomationAction[];
  /** Max interactive reply buttons for this rule's channel. `null` = no limit. */
  interactiveButtonMaxCount?: number | null;
  createdAt: string;
  updatedAt: string;
};

export type CommentAutomationListQuery = {
  channel?: CommentAutomationChannel | '';
  isEnabled?: boolean | '';
  page?: number;
  pageSize?: number;
};

export type CommentAutomationActionRequest = {
  type: CommentAutomationActionType;
  bodySource: CommentAutomationBodySource;
  templateText?: string | null;
  fallbackTemplateText?: string | null;
  instruction?: string | null;
  promptUseCase?: string | null;
  interactiveList?: ConversationInteractiveList | null;
  interactiveButtons?: ConversationInteractiveButtons | null;
};

export type CreateCommentAutomationRuleRequest = {
  name: string;
  channel: CommentAutomationChannel;
  channelAccountId?: string | null;
  isEnabled?: boolean;
  priority?: number;
  matchMode?: CommentAutomationMatchMode;
  keywords?: string[];
  conditionLogic?: AutomationConditionLogic;
  conditions?: AutomationConditionRequest[];
  onActionFailure?: AutomationActionFailureMode;
  externalContentId?: string | null;
  actions: CommentAutomationActionRequest[];
};

export type UpdateCommentAutomationRuleRequest = CreateCommentAutomationRuleRequest;

export type PagedCommentAutomationResult = {
  items: CommentAutomationRule[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasNext: boolean;
};

export type CommentAutomationContentOption = {
  externalContentId: string;
  channel: CommentAutomationChannel | string;
  contentType?: string | null;
  caption?: string | null;
  permalink?: string | null;
  thumbnailUrl?: string | null;
  mediaProductType?: string | null;
  createdAt?: string | null;
};

export type CommentAutomationContentOptionsQuery = {
  channel: CommentAutomationChannel;
  channelAccountId?: string | null;
  limit?: number;
  after?: string | null;
};

export type CommentAutomationContentOptionsResult = {
  items: CommentAutomationContentOption[];
  nextCursor?: string | null;
};

export const normalizeCommentAutomationContentOption = (payload: any): CommentAutomationContentOption => ({
  externalContentId: asString(payload?.externalContentId ?? payload?.ExternalContentId),
  channel: asString(payload?.channel ?? payload?.Channel, 'instagram'),
  contentType: asOptionalString(payload?.contentType ?? payload?.ContentType),
  caption: asOptionalString(payload?.caption ?? payload?.Caption),
  permalink: asOptionalString(payload?.permalink ?? payload?.Permalink),
  thumbnailUrl: asOptionalString(payload?.thumbnailUrl ?? payload?.ThumbnailUrl),
  mediaProductType: asOptionalString(payload?.mediaProductType ?? payload?.MediaProductType),
  createdAt: asOptionalString(payload?.createdAt ?? payload?.CreatedAt),
});

export const normalizeCommentAutomationContentOptions = (payload: any): CommentAutomationContentOptionsResult => {
  const rawItems = Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.Items)
      ? payload.Items
      : [];
  return {
    items: rawItems
      .map(normalizeCommentAutomationContentOption)
      .filter((item: CommentAutomationContentOption) => Boolean(item.externalContentId)),
    nextCursor: asOptionalString(payload?.nextCursor ?? payload?.NextCursor),
  };
};

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeInteractiveButtonMaxCount = (value: unknown): number | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeOptionalText = (value: unknown): string | undefined => {
  const normalized = asOptionalString(value);
  return normalized ?? undefined;
};

const createInteractiveDraftId = (prefix: 'row' | 'btn'): string =>
  `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const normalizeChannel = (value: unknown): CommentAutomationChannel =>
  String(value ?? '').trim().toLowerCase() === 'messenger' ? 'messenger' : 'instagram';

const normalizeMatchMode = (value: unknown): CommentAutomationMatchMode => {
  const normalized = String(value ?? '').trim().toLowerCase().replace('-', '_');
  if (normalized === 'contains') {
    return 'contains';
  }
  if (normalized === 'starts_with' || normalized === 'startswith') {
    return 'starts_with';
  }
  return 'equals';
};

const normalizeActionType = (value: unknown): CommentAutomationActionType =>
  String(value ?? '').trim().toLowerCase() === 'private_reply'
    ? 'private_reply'
    : 'public_comment_reply';

const normalizeBodySource = (
  value: unknown,
  interactiveList?: ConversationInteractiveList | null,
  interactiveButtons?: ConversationInteractiveButtons | null,
): CommentAutomationBodySource => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'ai') {
    return 'ai';
  }
  if (normalized === 'interactive' || interactiveList || interactiveButtons) {
    return 'interactive';
  }
  return 'template';
};

const normalizeInteractiveRow = (value: any): ConversationInteractiveListRow => ({
  id: asString(value?.id).trim(),
  title: asString(value?.title).trim(),
  description: normalizeOptionalText(value?.description),
});

const normalizeInteractiveSection = (value: any): ConversationInteractiveListSection => ({
  title: normalizeOptionalText(value?.title),
  rows: Array.isArray(value?.rows)
    ? value.rows.map(normalizeInteractiveRow).filter((row: ConversationInteractiveListRow) => row.id && row.title)
    : [],
});

export const normalizeInteractiveList = (value: any): ConversationInteractiveList | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const bodyText = asOptionalString(value?.bodyText);
  const buttonText = asOptionalString(value?.buttonText);
  const sections = Array.isArray(value?.sections)
    ? value.sections
      .map(normalizeInteractiveSection)
      .filter((section: ConversationInteractiveListSection) => section.rows.length > 0)
    : [];

  if (!bodyText || !buttonText || sections.length === 0) {
    return null;
  }

  return {
    headerText: normalizeOptionalText(value?.headerText),
    bodyText,
    footerText: normalizeOptionalText(value?.footerText),
    buttonText,
    sections,
  };
};

const normalizeInteractiveButtonItem = (value: any): ConversationInteractiveButtonItem => ({
  id: asString(value?.id).trim(),
  title: asString(value?.title).trim(),
});

export const normalizeInteractiveButtons = (value: any): ConversationInteractiveButtons | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const bodyText = asOptionalString(value?.bodyText);
  const buttons = Array.isArray(value?.buttons)
    ? value.buttons
      .map(normalizeInteractiveButtonItem)
      .filter((button: ConversationInteractiveButtonItem) => button.id && button.title)
    : [];

  if (!bodyText || buttons.length === 0) {
    return null;
  }

  return {
    headerText: normalizeOptionalText(value?.headerText),
    bodyText,
    footerText: normalizeOptionalText(value?.footerText),
    buttons,
  };
};

export const emptyInteractiveRow = (): ConversationInteractiveListRow => ({
  id: createInteractiveDraftId('row'),
  title: '',
  description: '',
});

export const emptyInteractiveSection = (): ConversationInteractiveListSection => ({
  title: '',
  rows: [emptyInteractiveRow()],
});

export const emptyInteractiveList = (): ConversationInteractiveList => ({
  headerText: '',
  bodyText: '',
  footerText: '',
  buttonText: 'Open list',
  sections: [emptyInteractiveSection()],
});

export const emptyInteractiveButton = (): ConversationInteractiveButtonItem => ({
  id: createInteractiveDraftId('btn'),
  title: '',
});

export const emptyInteractiveButtons = (): ConversationInteractiveButtons => ({
  headerText: '',
  bodyText: '',
  footerText: '',
  buttons: [emptyInteractiveButton()],
});

/** Editor-friendly drafts (keep empty rows/buttons for form editing). */
export const toInteractiveListDraft = (value?: ConversationInteractiveList | null): ConversationInteractiveList => {
  if (!value) {
    return emptyInteractiveList();
  }
  return {
    headerText: value.headerText ?? '',
    bodyText: value.bodyText ?? '',
    footerText: value.footerText ?? '',
    buttonText: value.buttonText ?? 'Open list',
    sections: value.sections.length > 0
      ? value.sections.map((section) => ({
        title: section.title ?? '',
        rows: section.rows.length > 0
          ? section.rows.map((row) => ({
            id: row.id || createInteractiveDraftId('row'),
            title: row.title ?? '',
            description: row.description ?? '',
          }))
          : [emptyInteractiveRow()],
      }))
      : [emptyInteractiveSection()],
  };
};

export const toInteractiveButtonsDraft = (
  value?: ConversationInteractiveButtons | null,
): ConversationInteractiveButtons => {
  if (!value) {
    return emptyInteractiveButtons();
  }
  return {
    headerText: value.headerText ?? '',
    bodyText: value.bodyText ?? '',
    footerText: value.footerText ?? '',
    buttons: value.buttons.length > 0
      ? value.buttons.map((button) => ({
        id: button.id || createInteractiveDraftId('btn'),
        title: button.title ?? '',
      }))
      : [emptyInteractiveButton()],
  };
};

export const serializeInteractiveListForRequest = (
  interactiveList: ConversationInteractiveList,
): ConversationInteractiveList | null => {
  const sections = interactiveList.sections
    .map((section) => ({
      title: section.title?.trim() || undefined,
      rows: section.rows
        .map((row) => ({
          id: row.id.trim() || createInteractiveDraftId('row'),
          title: row.title.trim(),
          description: row.description?.trim() || undefined,
        }))
        .filter((row) => row.title),
    }))
    .filter((section) => section.rows.length > 0);

  const bodyText = interactiveList.bodyText.trim();
  const buttonText = interactiveList.buttonText.trim();
  if (!bodyText || !buttonText || sections.length === 0) {
    return null;
  }

  return {
    headerText: interactiveList.headerText?.trim() || undefined,
    bodyText,
    footerText: interactiveList.footerText?.trim() || undefined,
    buttonText,
    sections,
  };
};

export const serializeInteractiveButtonsForRequest = (
  interactiveButtons: ConversationInteractiveButtons,
): ConversationInteractiveButtons | null => {
  const buttons = interactiveButtons.buttons
    .map((button) => ({
      id: button.id.trim() || createInteractiveDraftId('btn'),
      title: button.title.trim(),
    }))
    .filter((button) => button.title);

  const bodyText = interactiveButtons.bodyText.trim();
  if (!bodyText || buttons.length === 0) {
    return null;
  }

  return {
    headerText: interactiveButtons.headerText?.trim() || undefined,
    bodyText,
    footerText: interactiveButtons.footerText?.trim() || undefined,
    buttons,
  };
};

export type InteractiveActionValidationError =
  | 'interactiveListBodyRequired'
  | 'interactiveListButtonRequired'
  | 'interactiveListSectionRequired'
  | 'interactiveListRowRequired'
  | 'interactiveButtonsBodyRequired'
  | 'interactiveButtonsRequired';

export const validateInteractiveAction = (
  action: CommentAutomationAction,
): InteractiveActionValidationError | null => {
  if (action.bodySource !== 'interactive') {
    return null;
  }

  const mode: CommentAutomationInteractiveMode =
    action.interactiveMode
    ?? (action.interactiveButtons && !action.interactiveList ? 'buttons' : 'list');

  if (mode === 'buttons') {
    const draft = toInteractiveButtonsDraft(action.interactiveButtons);
    if (!draft.bodyText.trim()) {
      return 'interactiveButtonsBodyRequired';
    }
    if (!draft.buttons.some((button) => button.title.trim())) {
      return 'interactiveButtonsRequired';
    }
    return null;
  }

  const draft = toInteractiveListDraft(action.interactiveList);
  if (!draft.bodyText.trim()) {
    return 'interactiveListBodyRequired';
  }
  if (!draft.buttonText.trim()) {
    return 'interactiveListButtonRequired';
  }
  if (draft.sections.length === 0) {
    return 'interactiveListSectionRequired';
  }
  if (!draft.sections.some((section) => section.rows.some((row) => row.title.trim()))) {
    return 'interactiveListRowRequired';
  }
  return null;
};

export const toCommentAutomationActionRequest = (
  action: CommentAutomationAction,
): CommentAutomationActionRequest => {
  if (action.type === 'public_comment_reply') {
    if (action.bodySource === 'ai') {
      return {
        type: action.type,
        bodySource: 'ai',
        templateText: null,
        fallbackTemplateText: action.fallbackTemplateText?.trim() || null,
        instruction: action.instruction?.trim() || null,
        promptUseCase: action.promptUseCase?.trim() || null,
        interactiveList: null,
        interactiveButtons: null,
      };
    }

    return {
      type: action.type,
      bodySource: 'template',
      templateText: action.templateText?.trim() || null,
      fallbackTemplateText: null,
      instruction: null,
      promptUseCase: null,
      interactiveList: null,
      interactiveButtons: null,
    };
  }

  if (action.bodySource === 'interactive') {
    const mode: CommentAutomationInteractiveMode =
      action.interactiveMode
      ?? (action.interactiveButtons && !action.interactiveList ? 'buttons' : 'list');
    const interactiveList = mode === 'list'
      ? serializeInteractiveListForRequest(toInteractiveListDraft(action.interactiveList))
      : null;
    const interactiveButtons = mode === 'buttons'
      ? serializeInteractiveButtonsForRequest(toInteractiveButtonsDraft(action.interactiveButtons))
      : null;

    return {
      type: 'private_reply',
      bodySource: 'interactive',
      templateText: null,
      fallbackTemplateText: null,
      instruction: null,
      promptUseCase: null,
      interactiveList,
      interactiveButtons,
    };
  }

  return {
    type: 'private_reply',
    bodySource: action.bodySource === 'ai' ? 'ai' : 'template',
    templateText: action.bodySource === 'template' ? (action.templateText?.trim() || null) : null,
    fallbackTemplateText: action.bodySource === 'ai' ? (action.fallbackTemplateText?.trim() || null) : null,
    instruction: action.bodySource === 'ai' ? (action.instruction?.trim() || null) : null,
    promptUseCase: action.promptUseCase?.trim() || null,
    interactiveList: null,
    interactiveButtons: null,
  };
};

export const summarizeInteractiveAction = (action: CommentAutomationAction): {
  mode: CommentAutomationInteractiveMode;
  optionCount: number;
} | null => {
  if (action.bodySource !== 'interactive') {
    return null;
  }

  if (action.interactiveButtons && (!action.interactiveList || action.interactiveMode === 'buttons')) {
    return {
      mode: 'buttons',
      optionCount: action.interactiveButtons.buttons.length,
    };
  }

  if (action.interactiveList) {
    return {
      mode: 'list',
      optionCount: action.interactiveList.sections.reduce((total, section) => total + section.rows.length, 0),
    };
  }

  return {
    mode: action.interactiveMode === 'buttons' ? 'buttons' : 'list',
    optionCount: 0,
  };
};

export const normalizeCommentAutomationAction = (value: any): CommentAutomationAction => {
  const interactiveList = normalizeInteractiveList(value?.interactiveList);
  const interactiveButtons = normalizeInteractiveButtons(value?.interactiveButtons);
  const bodySource = normalizeBodySource(value?.bodySource, interactiveList, interactiveButtons);
  const type = normalizeActionType(value?.type);

  if (type === 'public_comment_reply') {
    const publicSource = bodySource === 'ai' ? 'ai' : 'template';
    return {
      type,
      bodySource: publicSource,
      templateText: publicSource === 'template' ? asOptionalString(value?.templateText) : null,
      fallbackTemplateText: publicSource === 'ai' ? asOptionalString(value?.fallbackTemplateText) : null,
      instruction: publicSource === 'ai' ? asOptionalString(value?.instruction) : null,
      promptUseCase: publicSource === 'ai' ? asOptionalString(value?.promptUseCase) : null,
      interactiveMode: null,
      interactiveList: null,
      interactiveButtons: null,
    };
  }

  if (bodySource === 'interactive') {
    const interactiveMode: CommentAutomationInteractiveMode =
      interactiveButtons && !interactiveList
        ? 'buttons'
        : interactiveList
          ? 'list'
          : String(value?.interactiveMode ?? '').trim().toLowerCase() === 'buttons'
            ? 'buttons'
            : 'list';

    return {
      type: 'private_reply',
      bodySource: 'interactive',
      templateText: null,
      fallbackTemplateText: null,
      instruction: null,
      promptUseCase: null,
      interactiveMode,
      interactiveList: toInteractiveListDraft(interactiveList),
      interactiveButtons: toInteractiveButtonsDraft(interactiveButtons),
    };
  }

  return {
    type: 'private_reply',
    bodySource,
    templateText: asOptionalString(value?.templateText),
    fallbackTemplateText: asOptionalString(value?.fallbackTemplateText),
    instruction: asOptionalString(value?.instruction),
    promptUseCase: asOptionalString(value?.promptUseCase),
    interactiveMode: null,
    interactiveList: null,
    interactiveButtons: null,
  };
};

export const normalizeCommentAutomationRule = (value: any): CommentAutomationRule => ({
  id: asString(value?.id),
  name: asString(value?.name),
  channel: normalizeChannel(value?.channel),
  channelAccountId: asOptionalString(value?.channelAccountId),
  isEnabled: Boolean(value?.isEnabled),
  priority: Number(value?.priority ?? 100),
  matchMode: normalizeMatchMode(value?.matchMode),
  keywords: Array.isArray(value?.keywords)
    ? value.keywords.map((item: unknown) => asString(item).trim()).filter(Boolean)
    : flattenConditionKeywords(normalizeAutomationConditions(value?.conditions)),
  conditionLogic: normalizeConditionLogic(value?.conditionLogic),
  onActionFailure: normalizeActionFailureMode(value?.onActionFailure),
  conditions: normalizeAutomationConditions(
    value?.conditions,
    Array.isArray(value?.keywords)
      ? value.keywords.map((item: unknown) => asString(item).trim()).filter(Boolean)
      : [],
    normalizeKeywordMatchMode(value?.matchMode),
  ),
  externalContentId: asOptionalString(value?.externalContentId),
  actions: Array.isArray(value?.actions)
    ? value.actions.map(normalizeCommentAutomationAction)
    : [],
  interactiveButtonMaxCount: normalizeInteractiveButtonMaxCount(value?.interactiveButtonMaxCount),
  createdAt: asString(value?.createdAt),
  updatedAt: asString(value?.updatedAt),
});

export const normalizePagedCommentAutomations = (value: any): PagedCommentAutomationResult => ({
  items: Array.isArray(value?.items) ? value.items.map(normalizeCommentAutomationRule) : [],
  page: Number(value?.page ?? 1),
  pageSize: Number(value?.pageSize ?? 25),
  totalCount: Number(value?.totalCount ?? value?.total ?? 0),
  hasNext: Boolean(value?.hasNext),
});

export const emptyAction = (type: CommentAutomationActionType = 'public_comment_reply'): CommentAutomationAction => ({
  type,
  bodySource: 'template',
  templateText: type === 'public_comment_reply' ? 'Detailed info sent to your DM.' : '',
  fallbackTemplateText: type === 'private_reply' ? '' : null,
  instruction: type === 'private_reply' ? '' : null,
  promptUseCase: null,
  interactiveMode: null,
  interactiveList: null,
  interactiveButtons: null,
});
